import {
  createAsyncThunk,
  createSlice,
  type PayloadAction
} from "@reduxjs/toolkit"
import { clearTokens } from "@/lib/api/client"
import type { LoginRequest } from "@/lib/api/services/auth"
import { authService } from "@/lib/api/services/auth"

export interface User {
  id: number
  name: string
  email: string
  createdAt: string
  updatedAt: string
  firstName?: string
  lastName?: string
  role?: string
}

interface AuthState {
  user: User | null
  token: string | null
  refreshToken: string | null
  isAuthenticated: boolean
  isLoading: boolean
  error: string | null
  sessionExpiry: number | null
}

const initialState: AuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  isLoading: false,
  error: null,
  sessionExpiry: null
}

export const loginUser = createAsyncThunk(
  "auth/loginUser",
  async (credentials: LoginRequest, { rejectWithValue }) => {
    try {
      const response = await authService.login(credentials)

      if (response.success && response.data) {
        const data = response.data
        const actualData =
          (data as unknown as Record<string, unknown>).data || data

        const user =
          (actualData as Record<string, unknown>).user ||
          (actualData as Record<string, unknown>).User ||
          null
        const token =
          (actualData as Record<string, unknown>).token ||
          (actualData as Record<string, unknown>).Token ||
          (actualData as Record<string, unknown>).access_token ||
          (actualData as Record<string, unknown>).accessToken ||
          null
        const refreshToken =
          (actualData as Record<string, unknown>).refreshToken ||
          (actualData as Record<string, unknown>).RefreshToken ||
          (actualData as Record<string, unknown>).refresh_token ||
          null
        let expiresIn =
          (actualData as Record<string, unknown>).expiresIn ||
          (actualData as Record<string, unknown>).ExpiresIn ||
          (actualData as Record<string, unknown>).expires_in ||
          3600

        if (typeof expiresIn === "number") {
          if (expiresIn > 86_400 * 1000) {
            expiresIn = Math.floor(expiresIn / 1000)
          } else if (expiresIn <= 0) {
            expiresIn = 3600
          }
        } else {
          expiresIn = 3600
        }

        if (token && !user) {
          return {
            user: {
              id: 1,
              name: "User",
              email: credentials.email,
              createdAt: new Date().toISOString(),
              updatedAt: new Date().toISOString(),
              role: "user"
            },
            token: token as string,
            refreshToken: refreshToken as string | null,
            expiresIn: expiresIn as number
          }
        }

        if (!token) {
          return rejectWithValue("No authentication token received")
        }

        return {
          user: user as User | null,
          token: token as string,
          refreshToken: refreshToken as string | null,
          expiresIn: expiresIn as number
        }
      }

      return rejectWithValue("Login failed")
    } catch (error: unknown) {
      // Extract error message from axios error response
      if (error && typeof error === "object" && "response" in error) {
        const axiosError = error as {
          response?: {
            data?: {
              message?: string
              error?: string
              errors?: unknown
            }
            status?: number
            statusText?: string
          }
          message?: string
        }

        // Try to get error message from response data
        const errorMessage =
          axiosError.response?.data?.message ||
          axiosError.response?.data?.error ||
          axiosError.response?.statusText ||
          axiosError.message ||
          "Login failed"

        // Handle specific status codes
        if (axiosError.response?.status === 401) {
          return rejectWithValue("Invalid email or password")
        }
        if (axiosError.response?.status === 429) {
          return rejectWithValue(
            "Too many login attempts. Please try again later"
          )
        }
        if (axiosError.response?.status === 403) {
          return rejectWithValue("Access forbidden")
        }
        if (axiosError.response?.status === 500) {
          return rejectWithValue("Server error. Please try again later")
        }

        return rejectWithValue(errorMessage)
      }

      // Fallback for non-axios errors
      const errorMessage =
        error instanceof Error ? error.message : "Login failed"
      return rejectWithValue(errorMessage)
    }
  }
)

export const logoutUser = createAsyncThunk("auth/logoutUser", () => {
  clearTokens()
  return true
})

export const checkSessionExpiry = createAsyncThunk(
  "auth/checkSessionExpiry",
  (_, { getState }) => {
    const state = getState() as { auth: AuthState }
    const { sessionExpiry, token } = state.auth

    if (!(token && sessionExpiry)) {
      return { isExpired: true }
    }

    const now = Date.now()
    const timeUntilExpiry = sessionExpiry - now

    if (timeUntilExpiry <= 5000) {
      return { isExpired: true }
    }

    return { isExpired: false, refreshed: false }
  }
)

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null
    },
    setUser: (state, action: PayloadAction<User>) => {
      state.user = action.payload
      state.isAuthenticated = true
    },
    clearAuth: (state) => {
      state.user = null
      state.token = null
      state.refreshToken = null
      state.isAuthenticated = false
      state.sessionExpiry = null
      state.error = null
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload }
      }
    },
    updateTokensFromRefresh: (
      state,
      action: PayloadAction<{ token: string; refreshToken?: string }>
    ) => {
      state.token = action.payload.token
      if (action.payload.refreshToken) {
        state.refreshToken = action.payload.refreshToken
      }
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.isLoading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.isLoading = false
        state.user = action.payload.user
        state.token = action.payload.token
        state.refreshToken = action.payload.refreshToken
        state.isAuthenticated = true

        const expiresInSeconds = action.payload.expiresIn as number
        const now = Date.now()
        state.sessionExpiry = now + expiresInSeconds * 1000

        state.error = null
        {
          const maxAgeSeconds = action.payload.expiresIn as number
          fetch("/api/auth/session", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: action.payload.token, maxAgeSeconds })
          })
        }
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.isLoading = false
        state.error = action.payload as string
        state.isAuthenticated = false
      })

    builder
      .addCase(logoutUser.pending, (state) => {
        state.isLoading = true
      })
      .addCase(logoutUser.fulfilled, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.sessionExpiry = null
        state.error = null
        fetch("/api/auth/session", { method: "DELETE" })
      })
      .addCase(logoutUser.rejected, (state) => {
        state.isLoading = false
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.sessionExpiry = null
      })

    builder.addCase(checkSessionExpiry.fulfilled, (state, action) => {
      if (action.payload.isExpired) {
        state.user = null
        state.token = null
        state.refreshToken = null
        state.isAuthenticated = false
        state.sessionExpiry = null
      }
    })
  }
})

export const {
  clearError,
  setUser,
  clearAuth,
  updateUser,
  updateTokensFromRefresh
} = authSlice.actions
export { authSlice }
